import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell, CheckCircle, Trash2, AlertCircle, Clock, Wrench, FileText,
  ScanLine, Share2, Info, Cog, Filter as FilterIcon, X,
} from "lucide-react";
import { api } from "../api/client";
import { PageHeader } from "../components/Layout";
import { EmptyState, ErrorState } from "../components/EmptyState";
import { mapNetworkError, formatDate } from "../lib/fleet";

const CATEGORY_META = {
  maintenance: { label: "Maintenance", icon: Wrench, tint: "var(--mf-brand)" },
  documents: { label: "Documents", icon: FileText, tint: "#8b5cf6" },
  ai: { label: "IA", icon: ScanLine, tint: "#f59e0b" },
  share: { label: "Partage", icon: Share2, tint: "#0ea5e9" },
  info: { label: "Informations", icon: Info, tint: "var(--mf-text-muted)" },
  system: { label: "Système", icon: Cog, tint: "var(--mf-text-secondary)" },
};

const SEVERITY_TINT = {
  critical: "#7f1d1d",
  urgent: "var(--mf-urgent-solid)",
  important: "var(--mf-upcoming-solid)",
  info: "var(--mf-text-muted)",
};

const FILTERS = [
  { key: "all", label: "Toutes" },
  { key: "unread", label: "Non lues" },
  { key: "maintenance", label: "Maintenance" },
  { key: "documents", label: "Documents" },
  { key: "ai", label: "IA" },
  { key: "share", label: "Partage" },
  { key: "system", label: "Système" },
];

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async (f = filter) => {
    try {
      const { data } = await api.get(`/notifications?filter=${f}`);
      setItems(data.items || []);
      setErrorMsg(null);
      setLoaded(true);
    } catch (e) {
      setErrorMsg(mapNetworkError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(filter); }, [load, filter]);

  const markRead = async (nid) => {
    try {
      await api.patch(`/notifications/${nid}/read`);
      setItems((prev) => prev.map((n) => n.id === nid ? { ...n, is_read: true } : n));
    } catch { toast.error("Impossible de marquer comme lue"); }
  };

  const markAll = async () => {
    try {
      await api.patch(`/notifications/read-all`);
      toast.success("Toutes marquées comme lues");
      load(filter);
    } catch { toast.error("Impossible"); }
  };

  const remove = async (nid) => {
    try {
      await api.delete(`/notifications/${nid}`);
      setItems((prev) => prev.filter((n) => n.id !== nid));
    } catch { toast.error("Suppression impossible"); }
  };

  const openAction = async (n) => {
    if (!n.is_read) markRead(n.id);
    if (n.action_target) navigate(n.action_target);
  };

  const empty = !loading && loaded && items.length === 0;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto" }} data-testid="notifications-page">
      <PageHeader
        title="Notifications"
        subtitle="Toutes les alertes intelligentes de votre flotte"
        right={
          items.some((n) => !n.is_read) ? (
            <button
              onClick={markAll}
              data-testid="notifs-mark-all"
              style={{
                padding: "8px 14px", borderRadius: 999,
                background: "var(--mf-bg-muted)", border: "none",
                color: "var(--mf-brand)", fontWeight: 700, fontSize: 12, cursor: "pointer",
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              <CheckCircle size={14} /> Tout marquer lu
            </button>
          ) : null
        }
      />

      <div style={{ padding: "0 20px 100px" }}>
        {/* Filter chips */}
        <div style={{
          display: "flex", gap: 8, overflowX: "auto",
          marginBottom: 16, paddingBottom: 4,
        }} data-testid="notifs-filters">
          {FILTERS.map((f) => {
            const active = f.key === filter;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`notifs-filter-${f.key}`}
                style={{
                  padding: "6px 14px", borderRadius: 999, whiteSpace: "nowrap",
                  background: active ? "var(--mf-brand)" : "var(--mf-bg-card)",
                  color: active ? "#fff" : "var(--mf-text)",
                  border: active ? "none" : "1px solid var(--mf-border)",
                  fontWeight: 700, fontSize: 12, cursor: "pointer", flexShrink: 0,
                }}
              >{f.label}</button>
            );
          })}
        </div>

        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{ height: 76, background: "var(--mf-bg-muted)", borderRadius: 14 }} />
            ))}
          </div>
        ) : errorMsg && !loaded ? (
          <ErrorState message={errorMsg} onRetry={() => { setLoading(true); load(filter); }} testID="notifs-error" />
        ) : empty ? (
          <EmptyState
            testID="notifs-empty"
            icon={<Bell size={64} color="var(--mf-border)" />}
            title="Aucune notification"
            subtitle="Vous êtes à jour ! Les alertes intelligentes s'afficheront ici automatiquement."
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {items.map((n) => (
              <NotifRow key={n.id} notif={n} onOpen={() => openAction(n)} onMarkRead={() => markRead(n.id)} onRemove={() => remove(n.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function NotifRow({ notif, onOpen, onMarkRead, onRemove }) {
  const meta = CATEGORY_META[notif.category] || CATEGORY_META.info;
  const Icon = meta.icon;
  const sevTint = SEVERITY_TINT[notif.severity] || SEVERITY_TINT.info;
  const isCritical = notif.severity === "critical" || notif.severity === "urgent";
  return (
    <div
      data-testid={`notif-row-${notif.id}`}
      style={{
        background: "var(--mf-bg-card)", borderRadius: 14, boxShadow: "var(--mf-shadow)",
        display: "flex", gap: 12, padding: 14, position: "relative", overflow: "hidden",
        opacity: notif.is_read ? 0.7 : 1,
      }}
    >
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: sevTint }} />
      <span style={{
        width: 36, height: 36, borderRadius: 12,
        background: `${meta.tint}18`, color: meta.tint, flexShrink: 0,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={16} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
            {!notif.is_read && (
              <span data-testid={`notif-unread-dot-${notif.id}`} style={{ width: 6, height: 6, borderRadius: 3, background: sevTint, flexShrink: 0 }} />
            )}
            <span style={{ fontSize: 14, fontWeight: 800, color: "var(--mf-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {notif.title}
            </span>
          </div>
          <span style={{ fontSize: 10, color: "var(--mf-text-muted)", fontWeight: 700, whiteSpace: "nowrap" }}>
            {formatDate(notif.created_at)}
          </span>
        </div>
        <div style={{ fontSize: 12, color: "var(--mf-text-secondary)", lineHeight: 1.4, marginBottom: 8 }}>
          {notif.message}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {notif.primary_label && notif.action_target && (
            <button
              onClick={onOpen}
              data-testid={`notif-action-${notif.id}`}
              style={{
                padding: "6px 12px", borderRadius: 999,
                background: "var(--mf-brand)", color: "#fff", border: "none",
                fontSize: 11, fontWeight: 700, cursor: "pointer",
              }}
            >{notif.primary_label}</button>
          )}
          {!notif.is_read && (
            <button
              onClick={onMarkRead}
              data-testid={`notif-mark-read-${notif.id}`}
              style={{
                padding: "6px 12px", borderRadius: 999,
                background: "transparent", color: "var(--mf-text-secondary)",
                border: "1px solid var(--mf-border)",
                fontSize: 11, fontWeight: 600, cursor: "pointer",
              }}
            >Marquer lue</button>
          )}
          {!isCritical && (
            <button
              onClick={onRemove}
              data-testid={`notif-remove-${notif.id}`}
              aria-label="Supprimer"
              style={{
                marginLeft: "auto", width: 28, height: 28, borderRadius: 14,
                background: "transparent", color: "var(--mf-text-muted)",
                border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
